/**
 * cues-colony.ts — colonisation lifecycle SFX (MFix3.6).
 *
 * Single-shot cue fired by `advancePendingColonisations` (and by the
 * direct colonise command, when a Coloniser lands on an uninhabited
 * planet). Distinct from `structure:complete` (a building finishing
 * construction) so the player gets unambiguous audio feedback that
 * the planet is theirs.
 *
 * Treatment: warm bell-tone (G major triad — G4 / B4 / D5 sustained
 * for ~1.2 s with bell-like exponential decay) plus a sub-bass thump
 * at the start to convey "ship landed, foundations laid". Routed
 * through the SFX bus; positional pan/attenuation supplied by the
 * dispatch caller (the planet's centre in tactical view).
 */

import type { AudioEngine } from './engine';
import type { CueVariation } from './registry';

const NEUTRAL: CueVariation = { pitchMul: 1, gainMul: 1 };

export function registerColonyCues(engine: AudioEngine): void {
  engine.registry.registerProcedural(
    'colony:established',
    'sfx',
    buildColonyEstablished,
  );
}

function buildColonyEstablished(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;

  // Sub-bass thump — sine sweep 100 → 50 Hz over the first 200 ms.
  // Returned as the engine's primary node.
  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(100 * variation.pitchMul, now);
  thump.frequency.exponentialRampToValueAtTime(50 * variation.pitchMul, now + 0.2);
  const thumpGain = ctx.createGain();
  thumpGain.gain.setValueAtTime(0.0001, now);
  thumpGain.gain.exponentialRampToValueAtTime(0.4 * variation.gainMul, now + 0.01);
  thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
  thump.connect(thumpGain);
  thumpGain.connect(target);
  thump.start(now);
  thump.stop(now + 0.41);

  // G major triad — G4 / B4 / D5. Each voice fades in just after the
  // thump so the bell rings as the ship "lands". 1.2 s tail with
  // exponential decay for a confident-but-not-loud arrival cue.
  const triad: ReadonlyArray<{ freq: number; delay: number; peak: number }> = [
    { freq: 392.0, delay: 0.05, peak: 0.28 }, // G4 root
    { freq: 493.88, delay: 0.08, peak: 0.22 }, // B4 third
    { freq: 587.33, delay: 0.11, peak: 0.18 }, // D5 fifth
  ];
  for (const { freq, delay, peak } of triad) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq * variation.pitchMul;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, now + delay);
    env.gain.exponentialRampToValueAtTime(peak * variation.gainMul, now + delay + 0.04);
    env.gain.exponentialRampToValueAtTime(0.0001, now + delay + 1.2);
    osc.connect(env);
    env.connect(target);
    osc.start(now + delay);
    osc.stop(now + delay + 1.21);
  }

  return thump;
}
